import React, { useCallback, useImperativeHandle, useRef, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { noop } from 'lodash';

/**
 * @typedef Action
 * @property {string} text
 * @property {() => void} onClick
 * @property {boolean} [destructive]
 */

/**
 * @callback OpenDialog
 * @param {string} title
 * @param {string | JSX.Element} description
 * @param {Action[]} [actions]
 * @param {{
 *  contentProps: import('@mui/material').DialogContentProps
 *  dismissible: boolean
 * }} [options]
 */

function throwUninitialized() {
    throw new Error('Dialog renderer not initialized');
}

/** @type {Action[]} */
const DefaultActions = [
    { text: 'Close', onClick: noop }
];

/**
 * Provides a dialog which can be managed imperatively.
 * @returns {[
 *  React.FunctionComponent<import('@mui/material').DialogProps>,
 *  OpenDialog,
 *  () => void
 * ]} Returns the tuple: [ImperativeDialog, openDialog, closeDialog]
 */
export default function useDialog() {
    const ref = useRef();

    const Renderer = useCallback(function Renderer(props) {
        const [{
            title = '',
            description = '',
            actions = DefaultActions,
            contentProps,
            dismissible = false,
        } = {}, setContent] = useState();
        const [isOpen, setOpen] = useState(false);

        const classes = useStyles();

        useImperativeHandle(ref, () => ({
            open(title, description, actions, options) {
                setContent({ title, description, actions, ...options });
                setOpen(true);
            },
            close() { setOpen(false); }
        }), []);

        return (
            <Dialog
                open={isOpen}
                onClose={dismissible ? () => setOpen(false) : undefined}
                {...props}
            >
                <DialogTitle>
                    {title}
                </DialogTitle>
                <DialogContent {...contentProps}>
                    {typeof description === 'string' ? (
                        <DialogContentText>
                            {description}
                        </DialogContentText>
                    ) : description}
                </DialogContent>
                <DialogActions>
                    {actions.map(({ text, onClick = noop, destructive }, index) => (
                        <Button
                            key={index}
                            onClick={() => { onClick(); setOpen(false); }}
                            color='primary'
                            className={destructive ? classes.buttonDestructive : undefined}
                        >
                            {text}
                        </Button>
                    ))}
                </DialogActions>
            </Dialog>
        );
    }, []);

    const openDialog = useCallback((...args) => {
        if (!ref.current) throwUninitialized();

        ref.current.open(...args);
    }, []);

    const closeDialog = useCallback(() => {
        if (!ref.current) throwUninitialized();

        ref.current.close();
    }, []);

    return [Renderer, openDialog, closeDialog];
}

const useStyles = makeStyles(theme => ({
    buttonDestructive: {
        color: theme.palette.error.main,
    }
}));